import { z } from 'zod';
import { Vals } from '~lib';
import { Common } from './common.model';

/**
 * @desc The report catalogue and downloads (UI round 2, W0.2 contract; Lane B backs it with GCS
 * pointers to the legacy document files and the Mongo `downloads` audit collection).
 *
 * The stored entry carries its object's location (bucket and path); this client-facing projection
 * deliberately does not, and the procedures declare it as their output schema, so a location that
 * leaks into a service result is stripped on the way out rather than shipped. A file is only ever
 * reached through `downloads.signedUrl`.
 *
 * Mapping from the portal fixture `DownloadItem`: `updated` (ISO date string) → `publishedAt` (a
 * `Date`), `kind` → `documentType` (the fixture's finer classification; `kind` here is where the
 * document belongs), `format` (`xlsx` | `pdf`) is derived from `contentType` / `fileName`.
 */
export namespace Downloads {
  /** @desc Where a document belongs: a standalone report, a region's page or a group's page. */
  export const entryKinds = {
    Report: 'report',
    MarketDocument: 'market-document',
    CompanyDocument: 'company-document',
  } as const;
  export type EntryKind = Vals<typeof entryKinds>;
  export const ALL_ENTRY_KINDS = Object.values(entryKinds) as [EntryKind, ...EntryKind[]];

  /** @desc What a document is, where known — the fixture's `DownloadKind`. */
  export const documentTypes = {
    FinancialModel: 'financial-model',
    CompanyUpdate: 'company-update',
    CallNotes: 'call-notes',
    RegulatorySummary: 'regulatory-summary',
    MarketReport: 'market-report',
  } as const;
  export type DocumentType = Vals<typeof documentTypes>;
  export const ALL_DOCUMENT_TYPES = Object.values(documentTypes) as [
    DocumentType,
    ...DocumentType[],
  ];

  /** @desc Most download events one `downloads.recent` call may return. */
  export const MAX_RECENT = 50;

  export const catalogueEntrySchema = z.object({
    id: z.string().min(1),
    title: z.string().min(1),
    description: z.string().min(1).optional(),
    kind: z.enum(ALL_ENTRY_KINDS),
    documentType: z.enum(ALL_DOCUMENT_TYPES).optional(),
    /** @desc The region a market document belongs to. */
    marketSlug: Common.slugSchema.optional(),
    /** @desc The operator group a company document belongs to. */
    groupSlug: Common.slugSchema.optional(),
    /** @desc The name the browser saves the file under. */
    fileName: z.string().min(1),
    contentType: z.string().min(1),
    /** @desc Absent where the object's size has not been read. */
    sizeBytes: z.number().int().min(0).optional(),
    publishedAt: z.date(),
  });
  export type CatalogueEntry = z.infer<typeof catalogueEntrySchema>;

  export const catalogueInputSchema = z
    .object({
      market: Common.slugSchema.optional(),
      group: Common.slugSchema.optional(),
      kind: z.enum(ALL_ENTRY_KINDS).optional(),
    })
    .default({});
  export type CatalogueInput = z.infer<typeof catalogueInputSchema>;

  /** @desc Newest `publishedAt` first; one entry per document (its latest active file). */
  export const catalogueSchema = z.array(catalogueEntrySchema);

  /** @desc Rows per `downloads.search` page. Fixed server-side so a page number means one thing. */
  export const SEARCH_PAGE_SIZE = 25;
  export const MAX_SEARCH_QUERY_LENGTH = 200;

  /**
   * @desc Staff file search: free text over title, file name and extracted text, narrowed by
   * market (region), group, kind and a `publishedAt` range (both ends inclusive). No query lists the
   * filtered files newest first.
   */
  export const searchInputSchema = z.object({
    query: z.string().trim().max(MAX_SEARCH_QUERY_LENGTH).optional(),
    market: Common.slugSchema.optional(),
    group: Common.slugSchema.optional(),
    kind: z.enum(ALL_ENTRY_KINDS).optional(),
    from: z.date().optional(),
    to: z.date().optional(),
    page: z.number().int().min(1).default(1),
  });
  export type SearchInput = z.input<typeof searchInputSchema>;
  export type SearchQuery = z.output<typeof searchInputSchema>;

  /**
   * @desc A search hit: the catalogue entry (still without any storage location) plus whether its
   * file is in the bucket, from the last backfill `--verify`. An entry never verified counts as
   * available, as `signedUrl` does.
   */
  export const searchItemSchema = catalogueEntrySchema.extend({ available: z.boolean() });
  export type SearchItem = z.infer<typeof searchItemSchema>;

  export const searchResultSchema = z.object({
    items: z.array(searchItemSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
  });
  export type SearchResult = z.infer<typeof searchResultSchema>;

  export const signedUrlInputSchema = z.object({ id: z.string().min(1) });
  export type SignedUrlInput = z.infer<typeof signedUrlInputSchema>;

  /** @desc A short-lived link to one catalogue entry's file. */
  export const signedUrlSchema = z.object({
    url: z.url({ protocol: /^https$/ }),
    expiresAt: z.date(),
  });
  export type SignedUrl = z.infer<typeof signedUrlSchema>;

  /** @desc One download by the caller, with the entry's title as it was when downloaded. */
  export const eventSchema = z.object({
    id: z.string().min(1),
    entryId: z.string().min(1),
    title: z.string().min(1),
    downloadedAt: z.date(),
  });
  export type Event = z.infer<typeof eventSchema>;

  export const recentInputSchema = z
    .object({ limit: z.number().int().min(1).max(MAX_RECENT).default(10) })
    .default({ limit: 10 });
  export type RecentInput = z.infer<typeof recentInputSchema>;

  /** @desc The caller's own downloads, newest first. */
  export const recentSchema = z.array(eventSchema);
}
